import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SealLogo } from "../components/StampHeader";

// Circle invite landing page. Invite links point here
// (scan.story-stamp.com/invite?token=…). This page deliberately does NOT read
// the invite: the token is a secret and is validated server-side only when the
// app accepts it (acceptInvite, Admin SDK). So the page's one job is to forward
// the token into the Story Stamp app via its custom URL scheme. Until Universal
// Links are provisioned this "Open" button is the bridge from web to app.

// TODO(launch): swap for the real App Store URL once the app is published.
const GET_APP_URL = "https://storystamp.com";

export default function Invite() {
  const [params] = useSearchParams();
  const token = (params.get("token") ?? "").trim();

  // storystamp://invite?token=… — the app's DeepLinkRouter reads `token`.
  // We forward `role` too when present, purely for parity with the link.
  const appLink = useMemo(() => {
    if (!token) return null;
    const q = new URLSearchParams({ token });
    const role = (params.get("role") ?? "").trim();
    if (role) q.set("role", role);
    return `storystamp://invite?${q.toString()}`;
  }, [token, params]);

  if (!appLink) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center text-center px-6 gap-4">
        <SealLogo className="w-16 h-16 opacity-60" />
        <h1 className="text-xl font-semibold text-textPrimary">
          This invite link looks incomplete
        </h1>
        <p className="text-textSecondary text-sm max-w-xs">
          Ask the person who invited you to send the link again — it may have
          been cut off when it was shared.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center text-center px-6 gap-5">
      <SealLogo className="w-20 h-20" />
      <h1 className="text-3xl font-semibold tracking-tight text-textPrimary">
        You're invited
      </h1>
      <p className="text-textSecondary max-w-sm">
        Someone would like to share their stories with you in their private
        circle on Story Stamp. Open the app to join — only people with this link
        can, and nothing is ever public.
      </p>
      <a
        href={appLink}
        className="mt-2 px-6 py-3 rounded-full bg-primary text-white font-medium"
      >
        Open in Story Stamp
      </a>
      <p className="text-textSecondary text-xs max-w-xs">
        Don't have the app yet?{" "}
        <a
          href={GET_APP_URL}
          className="text-primary underline-offset-2 hover:underline"
        >
          Get Story Stamp
        </a>
        , then tap this link again.
      </p>
    </div>
  );
}
